import type { ReactNode } from 'react';

import type { components } from '../api/reportSchema.gen';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatUsageCost } from '../lib/money';

type ReportSummary = components['schemas']['ReportSummary'];

function formatCount(n: number | undefined): string {
  return (n ?? 0).toLocaleString();
}

function formatCost(value: string | undefined): string {
  if (!value || value === '0' || value === '0.000000') return formatUsageCost('0');
  return formatUsageCost(value);
}

function SummaryCard({
  label,
  value,
  accent,
  icon,
}: {
  label: string;
  value: string;
  accent?: boolean;
  icon: ReactNode;
}) {
  return (
    <article className="report-stat-card">
      <div className="report-stat-card__head">
        <span className="report-stat-card__icon" aria-hidden="true">
          {icon}
        </span>
        <p className="usage-hero-kicker">{label}</p>
      </div>
      <p className={accent ? 'report-stat-value report-stat-value--accent' : 'report-stat-value'}>{value}</p>
    </article>
  );
}

function IconRequests() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconArrowDown() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconArrowUp() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCost() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 7v10M9.5 9.5c0-1.1 1.12-2 2.5-2s2.5.9 2.5 2-1.12 2-2.5 2.5-1.38.5-2.5 1.4-2.5 2.5s1.12 2 2.5 2 2.5-.9 2.5-2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconTokens() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16M4 12h10M4 17h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

export function ReportSummaryCards({ summary }: { summary: ReportSummary }) {
  const { dict } = useLanguage();

  if (summary.shape === 'in_out_cost') {
    return (
      <div className="report-summary-grid" role="group" aria-label={dict.reports.summaryAria}>
        <SummaryCard label={dict.reports.summaryRequests} value={formatCount(summary.requests)} icon={<IconRequests />} />
        <SummaryCard label={dict.reports.summaryInputTokens} value={formatCount(summary.tokens_input)} icon={<IconArrowDown />} />
        <SummaryCard label={dict.reports.summaryOutputTokens} value={formatCount(summary.tokens_output)} icon={<IconArrowUp />} />
        <SummaryCard
          label={dict.reports.summaryTotalCost}
          value={formatCost(summary.cost)}
          accent
          icon={<IconCost />}
        />
      </div>
    );
  }

  if (summary.shape === 'in_out') {
    return (
      <div className="report-summary-grid report-summary-grid--three" role="group" aria-label={dict.reports.summaryAria}>
        <SummaryCard label={dict.reports.summaryRequests} value={formatCount(summary.requests)} icon={<IconRequests />} />
        <SummaryCard label={dict.reports.summaryInputTokens} value={formatCount(summary.tokens_input)} icon={<IconArrowDown />} />
        <SummaryCard label={dict.reports.summaryOutputTokens} value={formatCount(summary.tokens_output)} icon={<IconArrowUp />} />
      </div>
    );
  }

  return (
    <div className="report-summary-grid report-summary-grid--three" role="group" aria-label={dict.reports.summaryAria}>
      <SummaryCard label={dict.reports.summaryRequests} value={formatCount(summary.requests)} icon={<IconRequests />} />
      <SummaryCard label={dict.reports.summaryTotalTokens} value={formatCount(summary.tokens_total)} icon={<IconTokens />} />
      <SummaryCard label={dict.reports.summaryTotalCost} value={formatCost(summary.cost)} accent icon={<IconCost />} />
    </div>
  );
}
